import { locale } from '../lib/locale';

/**
 * Essensplan (Küche 3): Essen → Woche, "Heute gibt's" on Heute and the wall, votes, allergies.
 * Glossary: Essensplan = meal plan, Essenszeit = dinner time, Vorlieben = food preferences.
 * German is the master.
 */
export const MEALS_TEXT = {
  de: {
    tab: 'Woche',
    thisWeek: 'Diese Woche',
    nextWeek: 'Nächste Woche',
    prevWeek: 'Vorige Woche',
    weekLabel: 'Essen der Woche',
    open: 'Noch offen',
    openHint: 'Antippen: Rezept oder etwas anderes',
    today: 'heute',
    at: (time: string) => `${time} Uhr`,
    // the day sheet
    planTitle: (day: string) => `Essen am ${day}`,
    fromRecipes: 'Aus euren Rezepten',
    searchRecipe: 'Rezept suchen',
    noRecipes: 'Noch keine Rezepte – schreibt einfach, was es gibt.',
    orFree: 'Oder einfach so',
    freeLabel: 'Was gibt’s?',
    freeExample: 'Pizza bestellen, bei Oma, Reste',
    time: 'Uhrzeit (wenn anders als sonst)',
    note: 'Notiz (optional)',
    noteExample: 'Wunsch von Ben',
    save: 'Eintragen',
    clear: 'Tag freimachen',
    toRecipe: 'Zum Rezept',
    cook: 'Kochen starten',
    // votes
    votesTitle: 'Wer freut sich?',
    yes: 'Freu mich',
    no: 'Lieber nicht',
    voteFor: (name: string, what: string) => `${name}: ${what}`,
    votes: (yes: number, no: number) =>
      [yes ? `${yes} freu${yes === 1 ? 't sich' : 'en sich'}` : '', no ? `${no} lieber nicht` : '']
        .filter(Boolean)
        .join(' · '),
    // allergies
    allergy: (name: string, word: string) => `Achtung, ${word}: ${name} verträgt das nicht`,
    dislike: (name: string, word: string) => `${word}: ${name} mag das nicht`,
    prefsButton: 'Vorlieben & Allergien',
    prefsTitle: 'Vorlieben & Allergien',
    prefsHint:
      'Larovi warnt im Essensplan, wenn ein Rezept etwas davon enthält. Mehrere mit Komma trennen.',
    allergies: 'Allergien, Unverträglichkeiten',
    allergiesExample: 'Nüsse, Laktose',
    dislikes: 'Mag nicht',
    dislikesExample: 'Pilze, Oliven',
    prefsSave: 'Speichern',
    prefsSaved: 'Gespeichert.',
    // dinner time
    mealTime: 'Essenszeit',
    mealTimeHint: 'Wann ihr meistens esst – „Heute gibt’s“ zeigt diese Uhrzeit.',
    // the week's ingredients
    toList: 'Zutaten auf die Liste',
    toListTitle: 'Zutaten der Woche',
    toListHint: 'Aus den Rezepten ab heute, gleiche Sachen zusammengezählt.',
    toListNone: 'Ab heute steht noch kein Rezept im Plan.',
    forDays: (days: string) => `für ${days}`,
    toListButton: (n: number) => (n === 1 ? '1 Sache auf die Liste' : `${n} Sachen auf die Liste`),
    added: (n: number) =>
      n === 1 ? '1 Sache steht auf der Liste.' : `${n} Sachen stehen auf der Liste.`,
    onListAlready: 'steht schon drauf',
    // Plus
    plusTitle: 'Der Essensplan gehört zu Larovi Plus',
    plusText:
      'Plant die Woche, seht „Heute gibt’s“ auf dem Handy und an der Wand, die Kinder stimmen ab. Eure Rezepte und die Einkaufsliste bleiben kostenlos.',
    // Heute and the wall
    todayMeal: (time: string) => `Heute gibt’s · ${time}`,
    todayOpen: 'Heute ist beim Essen noch nichts geplant.',
    toWeek: 'Zum Essensplan',
  },
  en: {
    tab: 'Week',
    thisWeek: 'This week',
    nextWeek: 'Next week',
    prevWeek: 'Previous week',
    weekLabel: 'Meals this week',
    open: 'Not planned yet',
    openHint: 'Tap: a recipe or something else',
    today: 'today',
    at: (time: string) => time,
    planTitle: (day: string) => `Meal on ${day}`,
    fromRecipes: 'From your recipes',
    searchRecipe: 'Search recipe',
    noRecipes: 'No recipes yet – simply write what you are having.',
    orFree: 'Or just like that',
    freeLabel: 'What are you having?',
    freeExample: 'Order pizza, at Grandma’s, leftovers',
    time: 'Time (if different from usual)',
    note: 'Note (optional)',
    noteExample: 'Ben’s wish',
    save: 'Add to plan',
    clear: 'Clear this day',
    toRecipe: 'Open recipe',
    cook: 'Start cooking',
    votesTitle: 'Who is looking forward to it?',
    yes: 'Yes, please',
    no: 'Rather not',
    voteFor: (name: string, what: string) => `${name}: ${what}`,
    votes: (yes: number, no: number) =>
      [yes ? `${yes} looking forward` : '', no ? `${no} rather not` : '']
        .filter(Boolean)
        .join(' · '),
    allergy: (name: string, word: string) => `Careful, ${word}: ${name} cannot have it`,
    dislike: (name: string, word: string) => `${word}: ${name} does not like it`,
    prefsButton: 'Preferences & allergies',
    prefsTitle: 'Preferences & allergies',
    prefsHint:
      'Larovi warns in the meal plan when a recipe contains any of these. Separate several with commas.',
    allergies: 'Allergies, intolerances',
    allergiesExample: 'Nuts, lactose',
    dislikes: 'Does not like',
    dislikesExample: 'Mushrooms, olives',
    prefsSave: 'Save',
    prefsSaved: 'Saved.',
    mealTime: 'Dinner time',
    mealTimeHint: 'When you usually eat – “Today we’re having” shows this time.',
    toList: 'Ingredients to the list',
    toListTitle: 'Ingredients for the week',
    toListHint: 'From the recipes from today on, the same things added up.',
    toListNone: 'There is no recipe in the plan from today on yet.',
    forDays: (days: string) => `for ${days}`,
    toListButton: (n: number) =>
      n === 1 ? 'Add 1 item to the list' : `Add ${n} items to the list`,
    added: (n: number) => (n === 1 ? '1 item is on the list.' : `${n} items are on the list.`),
    onListAlready: 'already on the list',
    plusTitle: 'The meal plan is part of Larovi Plus',
    plusText:
      'Plan the week, see “Today we’re having” on your phone and on the wall, and let the kids vote. Your recipes and the shopping list stay free.',
    todayMeal: (time: string) => `Today we’re having · ${time}`,
    todayOpen: 'Nothing is planned for dinner today.',
    toWeek: 'Open the meal plan',
  },
};

export const mealsText = MEALS_TEXT[locale];
